import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { ResourceType } from "permissions/resources/types";
import { LoginAccessType } from "permissions/resources/users/types";
import { z } from "zod";

export type LoginAccessJSON = z.infer<typeof LoginAccessSchema.LoginAccessJSONZ>;
export type LoginAccessParams = z.infer<typeof LoginAccessSchema.LoginAccessZ>;

export default class LoginAccessSchema {
  public static readonly LoginAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.LoginAccess),
    accessType: z.enum(LoginAccessType).nullish(),
  });

  public static readonly LoginAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.LoginAccess),
    accessType: z.enum(LoginAccessType).optional(),
  });

  public static validate(values: unknown): LoginAccessParams {
    return parseOrThrow(LoginAccessSchema.LoginAccessZ, values, {
      logError: true,
      schemaName: "LoginAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): LoginAccessJSON {
    return parseOrThrow(LoginAccessSchema.LoginAccessJSONZ, values, {
      logError: true,
      schemaName: "LoginAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is LoginAccessParams {
    try {
      LoginAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is LoginAccessJSON {
    try {
      LoginAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: LoginAccessJSON): LoginAccessParams {
    const parsed = parseOrThrow(LoginAccessSchema.LoginAccessJSONZ, data, {
      logError: true,
      schemaName: "LoginAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return LoginAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: LoginAccessParams): LoginAccessJSON {
    const parsed = LoginAccessSchema.validate(notification);
    return LoginAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(LoginAccessSchema.LoginAccessJSONZ, LoginAccessSchema.LoginAccessZ, {
    decode: (data) => ({
      ...PermissionResourceSchema.deserialize(data),
      resourceType: ResourceType.LoginAccess as const,
      accessType: data.accessType ?? undefined,
    }),
    encode: (resource) => ({
      ...PermissionResourceSchema.serialize(resource),
      resourceType: ResourceType.LoginAccess as const,
      accessType: resource.accessType,
    }),
  });
}
